import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { EventService } from '../core/services/event.service';
import { AnalyticsService } from '../core/services/analytics.service';
import { InviteCardExportService } from '../core/services/invite-card-export.service';
import { ToastService } from '../core/services/toast.service';
import { WhatsappUploadService } from '../core/services/whatsapp-upload.service';
import { InviteCardService } from '../core/services/invite-card.service';
import { InviteCodeStore } from '../core/services/invite-code-store.service';
import { QrRenderService } from '../core/services/qr-render.service';
import { EventEditLauncherService } from '../core/services/event-edit-launcher.service';
import { InvitationCardDesignerComponent } from '../invitation-card-designer/invitation-card-designer.component';
import { CoverImageComponent } from '../cover-image/cover-image.component';
import { CanvaTemplate, CANVA_TEMPLATES } from '../core/data/canva-templates';
import { environment } from '../../environments/environment';
import { inviteLinkFor, serverQrMatchesInviteLink } from '../core/utils/invite-link';
import { CardTemplate, CardTemplateId, DEFAULT_TEMPLATE_ID, getCardTemplate, INVITE_CARD_TEMPLATES } from './invite-card-templates';
import { captureInviteOverride, inviteOverrideAllowed, keptInviteOverride } from './invite-ab-override';

/** Shown in every template when the event has no cover of its own. */
const FALLBACK_COVER = 'assets/images/default_cover.png';

/** Which event the stored `event_hash` belongs to — set wherever the hash is stored. */
export const INVITE_HASH_EVENT_KEY = 'event_hash_event_id';

/** A small, category-spanning sample — the full list lives behind "See all templates". */
const CARD_TEMPLATE_IDS = ['wedding-1', 'wedding-3', 'birthday-1', 'DAHFDTzAq4k', 'DAG-SGor8SU'];

export type ShareChannel = 'whatsapp' | 'sms' | 'email' | 'more';

/** iPadOS 13+ reports itself as MacIntel, hence the touch-point check. */
function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** What the host needs to share an event that has just been created. */
export interface InviteShareData {
  eventId: any;
  eventName: string;
  /** Pre-formatted date line, e.g. "Sat 5 Sep, 8:45 PM". Only used when there is no `eventStartTime`. */
  endSummary?: string;
  /** See InviteShareComponent.eventStartTime. The card's date line. */
  eventStartTime?: any;
  /** Distinguishes the create sheet from the deferred landing page in analytics. */
  surface?: string;
  /**
   * 'created' (default) is the post-creation moment — "Event created" tag,
   * "Send it to friends before you forget". 're-invite' is this same screen
   * opened later for an event that already has guests (event-detail's
   * "Invite Guests", event settings, …), which needs different copy since
   * nothing was just created.
   */
  mode?: 'created' | 're-invite';
  /** See InviteShareComponent.coverImageUrl. Only the create sheet supplies this today. */
  coverImageUrl?: string;
  /** See InviteShareComponent.hostName. */
  hostName?: string;
  /** See InviteShareComponent.inviteCode. */
  inviteCode?: string;
  /** See InviteShareComponent.viewerCode. */
  viewerCode?: string;
  /**
   * The event's hex (create's viewer_hex). With mode 'created' it adds the
   * "Edit details" link: right after creating is when a host spots a typo, and
   * without a way in they were creating the event again to fix it.
   */
  eventHex?: string;
}

/** The dialog's close result when "Edit details" took the host to the editor. */
export const INVITE_CLOSED_FOR_EDIT = 'edit-details';

/**
 * The share-your-invite step: card, link, and named destinations.
 *
 * Extracted from CreateEventSheet's step 4 so the deferred-login page can show
 * the same screen rather than a second implementation of it. Both surfaces are
 * the same moment — an event exists and nobody has been invited to it yet — and
 * the whole album depends on what happens here, so they must not drift apart.
 *
 * Works two ways. The sheet renders it inline (`<app-invite-share [eventId]…>`);
 * the deferred page opens it through MatDialog, which passes the same fields as
 * dialog data. Everything below reads the resolved values, not the inputs, so
 * neither path is the special case.
 */
@Component({
  selector: 'app-invite-share',
  templateUrl: './invite-share.component.html',
  styleUrls: ['./invite-share.component.scss'],
})
export class InviteShareComponent implements OnInit, AfterViewInit, OnDestroy {

  /** CSS width the card is laid out at for export — see captureCard(). */
  private static readonly EXPORT_CARD_WIDTH = 340;
  /** Saved PNG width; A6 at a bit over 300dpi (see InviteCardExportService.toFile). */
  private static readonly EXPORT_PIXEL_WIDTH = 1400;
  /** The link-preview card: square, 1200px, the size WhatsApp and iMessage render well. */
  private static readonly PREVIEW_PIXEL_WIDTH = 1200;
  /** Quiet time after the last change of look/cover before the preview card is uploaded. */
  private static readonly PREVIEW_SAVE_DELAY_MS = 900;
  /** Longest a single invite-link fetch may take before it counts as failed. */
  private static readonly QR_TIMEOUT_MS = 10000;
  /** Automatic retries before the host is shown "Try again". */
  private static readonly QR_MAX_RETRIES = 2;

  @Input() eventId: any;
  @Input() eventName = '';
  @Input() endSummary = '';
  @Input() surface = 'sheet';
  /** See InviteShareData.mode. */
  @Input() mode: 'created' | 're-invite' = 'created';
  /**
   * Tightens the card, QR and share row to fit the create sheet's step-4 body.
   * Set by the sheet; the dialog has room for the full-size values.
   */
  @Input() compact = false;

  /**
   * The event's cover. Every template is built on it; without one the card
   * falls back to the stock default cover (see cardCover).
   */
  @Input() coverImageUrl = '';

  /** Accepted from callers for the card's host line; the current templates don't print it. */
  @Input() hostName = '';

  /**
   * The event's collaborator and view-only codes, when the caller has them
   * (event-detail does, from the event's details for its host). With them
   * both links are on screen the moment it opens; see seedFromKnownCodes.
   */
  @Input() inviteCode = '';
  @Input() viewerCode = '';

  /**
   * The event's start time (epoch ms/seconds, a Date, or an ISO string), shown
   * as the card's date. Every caller passes it, so the card reads the same
   * right after create as it does when the invite is reopened. Create used to
   * pass the end time instead, and the card changed from 7 PM to 3 PM between
   * the two (QA 0.0.607).
   */
  @Input() eventStartTime: any = null;

  /** Normalises the many shapes a timestamp arrives in to epoch ms. */
  private static toEpochMs(value: any): number | null {
    if (value == null || value === '') return null;
    if (value instanceof Date) return value.getTime();
    const n = Number(value);
    // Seconds vs. ms: anything before year 2000 in ms is almost certainly
    // meant as seconds instead.
    if (!isNaN(n)) return n < 946684800000 ? n * 1000 : n;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed.getTime();
  }

  /** `eventStartTime` formatted; `endSummary` only when there is no start time. */
  private get resolvedDateLabel(): string {
    const ms = InviteShareComponent.toEpochMs(this.eventStartTime);
    if (ms == null) return this.endSummary || '';
    const dt = DateTime.fromMillis(ms);
    return dt.isValid ? dt.toFormat("ccc d LLL, h:mm a") : (this.endSummary || '');
  }

  /** The card's date line. */
  get cardDate(): string {
    return this.resolvedDateLabel;
  }

  /** Gates the redesigned screen (template card, share actions, printed card). */
  readonly newInviteDesignEnabled: boolean = environment.features.newInviteDesign;
  /**
   * Gates the "Guests can" switch (see environment.ts). Off, `access` never
   * leaves 'upload', so every link, QR and message is the collaborator one.
   */
  readonly viewerInviteEnabled: boolean = environment.features.viewerInviteLink;
  readonly templates = INVITE_CARD_TEMPLATES;
  selectedTemplateId: CardTemplateId = DEFAULT_TEMPLATE_ID;

  get cardTemplate(): CardTemplate {
    return getCardTemplate(this.selectedTemplateId);
  }

  /** A cover the host just picked on this screen, shown before the caller's input catches up. */
  private coverOverride = '';
  coverSaving = false;

  /** What every template draws its photo from. */
  get cardCover(): string {
    return this.coverOverride || this.coverImageUrl || FALLBACK_COVER;
  }

  /** CSS for the template's photo layers (`--img`). */
  get cardCoverCss(): string {
    return `url("${this.cardCover.replace(/"/g, '%22')}")`;
  }

  /**
   * The event name's size in card units (1% of card width), stepped by length
   * so a long name wraps to two or three lines instead of overflowing, then
   * scaled to the template's face.
   */
  nameUnits(template: CardTemplate): number {
    const len = (this.eventName || '').length;
    const base = len <= 10 ? 11.5 : len <= 16 ? 9.6 : len <= 24 ? 8.2 : len <= 34 ? 7 : 6;
    return base * template.nameScale;
  }

  /**
   * The event name as the card prints it. A spaced dash ("Greece 2026 - for
   * family") is tied to the word before it, so a wrap never starts a line
   * with the dash; the balanced wrap then splits the name either side of it.
   */
  get cardName(): string {
    return (this.eventName || '').replace(/\s+([-–—])\s+/g, ' $1 ');
  }

  /** Changing the cover here needs the save endpoint (it is the event's real cover). */
  get canEditCover(): boolean {
    return this.inviteCardService.enabled && !!this.eventId;
  }

  /**
   * The one red button. On a phone with a share sheet it opens the sheet
   * (WhatsApp, Messages, Instagram… one tap away); on desktop, where the link
   * gets pasted into WhatsApp Web, email or a group chat, it copies the link.
   */
  readonly shareFirst: boolean = typeof navigator !== 'undefined'
    && typeof (navigator as any).share === 'function'
    && !!window.matchMedia?.('(pointer: coarse)').matches;

  /**
   * Hosts in India get "Send on WhatsApp" as the red button. They mostly post
   * the card in WhatsApp groups, and after the 24 Sep redesign their send rate
   * fell (62% to 49%) while the US held at 76% (PostHog, 2026-10-23). Read from
   * the phone's time zone: no permission, no network call. A wrong guess only
   * swaps which button is red; every option is still on screen.
   */
  readonly indiaHost: boolean = (() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      return tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta';
    } catch {
      return false;
    }
  })();

  /**
   * Which just-created screen this host sees: the invite A/B test (`inviteAb`
   * in environment.ts, BUILD_PLAN_INVITE_AB_AND_CREATE_2026-11-03.md Part 2).
   * 'a' is today's screen, and what everyone outside the test gets. Settled
   * once in ngOnInit (settleInviteVersion) so it never changes under the host.
   */
  inviteVersion: 'a' | 'b' = 'a';
  /** In the test population, whichever version they drew. False for ?invite= test views. */
  inviteAbEligible = false;
  /** Eligible, but the PostHog kill switch sent them to A. */
  private inviteAbKilled = false;
  /** The test switch (?invite=a|b) picked the version. */
  private inviteAbForced = false;

  get inviteB(): boolean {
    return this.inviteVersion === 'b';
  }

  /** B's "What they'll see on WhatsApp" Edit: the look and cover pickers, open or not. */
  previewEditing = false;

  /** The host's initial in B's reel-goal row. */
  get hostInitial(): string {
    return ((this.hostName || '').trim().charAt(0) || 'Y').toUpperCase();
  }

  /** B's preview bubble prints the link the way WhatsApp does: no scheme. */
  get inviteLinkText(): string {
    return this.inviteLink.replace(/^https?:\/\//i, '');
  }

  /**
   * The words in B's bubble: the message itself. The WhatsApp-route line that
   * follows the link in the real message is left out, as on the board.
   */
  get previewMessage(): string {
    return this.shareText;
  }

  /** The bubble's timestamp: now, as the chat would stamp it. */
  readonly previewTime = DateTime.now().toLocaleString(DateTime.TIME_SIMPLE);

  togglePreviewEdit(): void {
    this.previewEditing = !this.previewEditing;
    if (this.previewEditing) this.analytics.capture('invite_preview_edit_opened', { surface: this.surface, ...this.abProps });
  }

  /**
   * The test switch: `?invite=a` or `?invite=b`, only where
   * inviteOverrideAllowed(). main.ts keeps it for the tab before the router
   * rewrites the URL (invite-ab-override.ts); reading again here also catches
   * a `?invite=` added to the hash after the app loaded.
   */
  private static readInviteOverride(): 'a' | 'b' | null {
    if (!inviteOverrideAllowed()) return null;
    captureInviteOverride();
    return keptInviteOverride();
  }

  /**
   * Only the dialog that opens right after Create Event (start page and the
   * in-app create sheet), never manage-event's inline panel, which also runs
   * as mode 'created'. Only India hosts, only accounts above the cutoff:
   * create stores the new host's user_tbl.id under `u` just before this opens
   * (create-event-form), and ids only go up. Even id = A, odd = B, so a host
   * gets the same version on every event and device. The PostHog flag can
   * only force A; a flag that hasn't loaded counts as off.
   *
   * The test switch skips every rule and turns any event's invite into the
   * just-created screen, so testing needs no new event each time.
   */
  private settleInviteVersion(): void {
    const ab = environment.inviteAb;
    if (!ab || !this.isDialog || !this.newInviteDesignEnabled) return;

    const forced = InviteShareComponent.readInviteOverride();
    if (forced) {
      this.inviteAbForced = true;
      this.mode = 'created';
      this.inviteVersion = forced;
      return;
    }

    if (!ab.enabled || this.mode !== 'created' || !this.indiaHost) return;
    const userId = Number(localStorage.getItem('u'));
    if (!Number.isInteger(userId) || userId <= ab.minUserId) return;

    this.inviteAbEligible = true;
    this.inviteAbKilled = !!ab.remoteKillSwitchFlag && this.analytics.isFlagOn(ab.remoteKillSwitchFlag);
    this.inviteVersion = !this.inviteAbKilled && userId % 2 === 1 ? 'b' : 'a';
  }

  /** On every invite event, so A and B can be told apart in PostHog. */
  private get abProps(): Record<string, any> {
    return {
      invite_version: this.inviteVersion,
      invite_ab_eligible: this.inviteAbEligible,
      ...(this.inviteAbKilled ? { invite_ab_killed: true } : {}),
      ...(this.inviteAbForced ? { invite_ab_forced: true } : {}),
    };
  }

  /** The full-size look at the card, opened by tapping it. */
  zoomOpen = false;
  private swipeStartX: number | null = null;
  private swipedAt = 0;

  /**
   * The sheet doesn't own its own dialog when embedded inline, so it can't
   * close+navigate itself for "See all templates" — the host (create-event
   * sheet) does that. Ignored in dialog mode, which navigates directly.
   */
  @Output() browseTemplates = new EventEmitter<void>();

  /** True only when opened as a dialog, which is the only mode that can close itself. */
  readonly isDialog: boolean;

  /**
   * What the shared link lets guests do: 'upload' is the collaborator link,
   * 'view' the view-only one. Opens on 'upload' every time — that is the link
   * that fills the album — and the host switches for people who should only
   * look. Everything that hands a link out (share, copy, the printed card,
   * plain QR, the card designer) reads inviteLink / inviteQr, which follow it.
   */
  access: 'upload' | 'view' = 'upload';

  /** The collaborator code's link and QR: guests can add photos. */
  private collabLink = '';
  private collabQr = '';
  /**
   * The view-only code's. Never seeded from the shared `viewer_hash` key:
   * branding settings writes it for whichever event it was editing, without
   * the event id that guards `event_hash`, so it can belong to another event.
   * The per-event InviteCodeStore is fine.
   */
  private viewerLink = '';
  private viewerQr = '';
  /** getEventQR answered without a view-only code, so there is nothing to switch to. */
  viewerUnavailable = false;

  get inviteLink(): string {
    return this.access === 'view' ? this.viewerLink : this.collabLink;
  }

  get inviteQr(): string {
    return this.access === 'view' ? this.viewerQr : this.collabQr;
  }

  qrLoading = false;
  /** Every attempt to fetch the links failed. */
  private fetchFailed = false;
  /** …and the selected link isn't on hand from earlier, so the hero button offers "Try again". */
  get linkFailed(): boolean {
    return this.fetchFailed && !this.inviteLink;
  }
  private qrSub?: Subscription;
  copied = false;

  /** Empty until the token comes back, or forever if the feature is off. */
  whatsappUploadUrl = '';
  private whatsappUploadLoad: Promise<void> | null = null;
  /** "Sep 18, 3:45 PM" — shown next to the link so the host knows when to regenerate it. */
  whatsappUploadValidityLabel = '';
  whatsappCopied = false;

  /**
   * The sheet has two internal screens: the link/share view, and — behind
   * "Printed cards for the venue" — the QR card plus the ways to turn it into
   * something physical. Kept as one component rather than a route so it still
   * works embedded inline in the create sheet.
   */
  screen: 'invite' | 'printed' = 'invite';
  /** Gates "design your own card" + the Canva strip — see environment.ts. */
  readonly cardDesignerEnabled: boolean = environment.features.cardDesigner;
  readonly cardTemplates: CanvaTemplate[] =
    CANVA_TEMPLATES.filter(t => CARD_TEMPLATE_IDS.includes(t.id));

  /** Legacy body only: the old printed card. */
  @ViewChild('inviteCardEl') private inviteCardEl?: ElementRef<HTMLElement>;
  /** New design: the printed card (template + QR panel) that Download/Share rasterise. */
  @ViewChild('printCardEl') private printCardEl?: ElementRef<HTMLElement>;
  /** New design: the big square card, rasterised as the link preview. */
  @ViewChild('previewCardEl') private previewCardEl?: ElementRef<HTMLElement>;
  private cardFile: File | null = null;

  private previewTimer?: ReturnType<typeof setTimeout>;
  private previewSaving = false;
  private previewPending = false;
  /** What the last successfully saved preview showed, so an unchanged card isn't re-uploaded. */
  private lastPreviewSignature = '';
  /** Why the pending preview upload was scheduled — tags invite_card_saved / _failed. */
  private previewTrigger: 'initial' | 'template' | 'cover' = 'initial';

  /**
   * What happened while the screen was open, sent once as invite_share_ended
   * when it goes away — however it goes away (close button, backdrop, the
   * create sheet moving on, navigation). Replaces invite_share_closed, which
   * only fired from the dialog's close button.
   */
  private session = {
    openedAt: Date.now(),
    shareCount: 0,
    channels: new Set<string>(),
    templatesTried: new Set<string>(),
    coverPickerOpened: false,
    coverChanged: false,
    zoomed: false,
    printedOpened: false,
    viewOnlySelected: false,
  };

  constructor(
    private eventService: EventService,
    private analytics: AnalyticsService,
    private cardExport: InviteCardExportService,
    private inviteCardService: InviteCardService,
    private toastService: ToastService,
    private whatsappUpload: WhatsappUploadService,
    private inviteCodes: InviteCodeStore,
    private qrRender: QrRenderService,
    private dialog: MatDialog,
    private router: Router,
    private zone: NgZone,
    private editLauncher: EventEditLauncherService,
    @Optional() private dialogRef?: MatDialogRef<InviteShareComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) data?: InviteShareData,
  ) {
    // Not `!!dialogRef`: the sheet that embeds this component inline is
    // itself opened via MatDialog, and MatDialogRef is an untyped DI token —
    // this component would otherwise inherit the *ancestor* sheet's ref and
    // wrongly think it was opened as its own dialog. MAT_DIALOG_DATA is only
    // ever set (non-null) by the `dialog.open(InviteShareComponent, {data})`
    // call the deferred-login page makes, so it reliably tells the two apart.
    // Not `!!data` either: app.module and shared.module provide MAT_DIALOG_DATA
    // as `{}` at the root, so a routed page (manage-event) gets that empty
    // object, counted as a dialog, and showed dialog chrome whose Close threw.
    // openInviteShareDialog always passes an eventId; the empty `{}` never has one.
    this.isDialog = !!data?.eventId;
    if (data) {
      this.eventId = data.eventId;
      this.eventName = data.eventName || '';
      this.endSummary = data.endSummary || '';
      this.surface = data.surface || 'dialog';
      this.mode = data.mode || 'created';
      this.coverImageUrl = data.coverImageUrl || '';
      this.hostName = data.hostName || '';
      this.eventStartTime = data.eventStartTime ?? null;
      this.inviteCode = data.inviteCode || '';
      this.viewerCode = data.viewerCode || '';
      this.eventHex = data.eventHex || '';
    }
  }

  /** See InviteShareData.eventHex. */
  eventHex = '';

  /** "Edit details" under the title: only right after creating, only with somewhere to go. */
  get canEditDetails(): boolean {
    return this.isDialog && this.mode === 'created' && !!this.eventHex && this.editLauncher.enabled;
  }

  /**
   * Closes with INVITE_CLOSED_FOR_EDIT so the opener skips its usual landing on
   * the event page, then opens the editor. If the event can't be loaded, the
   * event page is still the right place to end up.
   */
  async editDetails(): Promise<void> {
    this.analytics.capture('edit_details_opened', { surface: this.surface, event_id: this.eventId });
    this.dialogRef?.close(INVITE_CLOSED_FOR_EDIT);
    const opened = await this.editLauncher.openDetails(this.eventHex);
    if (!opened) this.router.navigate(['/event-detail/true']);
  }

  ngOnInit(): void {
    this.settleInviteVersion();
    // B on a computer is a side-by-side layout a little wider than A's (InviteBDesktop2).
    if (this.inviteB && window.innerWidth > 768) this.dialogRef?.updateSize('920px');
    this.seedFromKnownCodes();
    this.loadInviteDetails();
    this.loadWhatsappUpload();
    this.session.templatesTried.add(this.selectedTemplateId);
    // The denominator for "of hosts who see this screen, how many share?".
    this.analytics.capture('invite_share_viewed', {
      surface: this.surface,
      mode: this.mode,
      template: this.selectedTemplateId,
      has_cover: !!this.coverImageUrl,
      hero_action: !this.newInviteDesignEnabled ? 'legacy'
        : !this.shareFirst ? 'copy_link' : (this.indiaHost || this.inviteB) ? 'whatsapp' : 'share_sheet',
      india_host: this.indiaHost,
      card_preview_enabled: this.inviteCardService.enabled,
      is_dialog: this.isDialog,
      new_design: this.newInviteDesignEnabled,
      // false = the host saw "Preparing your link…" until getEventQR answered.
      link_ready_on_open: !!this.inviteLink,
      ...this.abProps,
    });
  }

  ngAfterViewInit(): void {
    // Upload the default look straight away, so a host who shares without
    // touching anything still sends the card rather than the bare cover.
    this.schedulePreviewSave();
  }

  ngOnDestroy(): void {
    clearTimeout(this.previewTimer);
    this.qrSub?.unsubscribe();
    this.analytics.capture('invite_share_ended', {
      surface: this.surface,
      mode: this.mode,
      shared: this.session.shareCount > 0,
      share_count: this.session.shareCount,
      channels: Array.from(this.session.channels),
      templates_tried: this.session.templatesTried.size,
      final_template: this.selectedTemplateId,
      cover_picker_opened: this.session.coverPickerOpened,
      cover_changed: this.session.coverChanged,
      zoomed: this.session.zoomed,
      printed_opened: this.session.printedOpened,
      view_only_selected: this.session.viewOnlySelected,
      final_access: this.access,
      seconds_open: Math.round((Date.now() - this.session.openedAt) / 1000),
      ...this.abProps,
    });
  }

  /**
   * Fetched alongside the QR, not in series with it — the card is what the host
   * is waiting to see, and a slow token must not hold it up. Failure is silent
   * by design: the row simply never appears and the link invite is unaffected.
   *
   * Uses the multi-use INVITE token, not the single-use quick-upload one —
   * this link travels inside an invite that may be opened by many different
   * guests over days, so it can't die after the first one.
   */
  private loadWhatsappUpload(): void {
    this.whatsappUploadLoad = this.whatsappUpload.getInviteLink(this.eventId, this.eventName)
      .then(link => {
        if (!link) return;
        this.whatsappUploadUrl = link.url;
        this.whatsappUploadValidityLabel = link.validityLabel || '';
      })
      .catch(() => { /* no WhatsApp route: invites simply go without that line */ });
  }

  /**
   * Shows the link straight away when its code is already stored for this
   * event (create-event and event-detail both store it), then fetches the full
   * QR payload quietly behind it. The fetch also populates the localStorage
   * keys the rest of the app (invite dialog, event-detail) expects to find.
   */
  /**
   * Builds both links on the spot from codes the app already holds, so the
   * screen opens ready instead of on "Preparing your link…". A link is just
   * inviteLinkFor(code); only the code comes from the server, and it
   * reaches us from the caller (inviteCode / viewerCode), the per-event store
   * (InviteCodeStore), or — collaborator only — the older shared `event_hash`
   * key when it was stored for this event. The QRs are drawn here too.
   */
  private seedFromKnownCodes(): void {
    this.inviteCodes.remember(this.eventId, this.inviteCode, this.viewerCode);
    const stored = this.inviteCodes.get(this.eventId);
    const collab = this.inviteCode || stored.collab;
    const viewer = this.viewerCode || stored.viewer;
    if (!this.collabLink && collab) this.collabLink = inviteLinkFor(collab);
    if (!this.viewerLink && viewer) this.viewerLink = inviteLinkFor(viewer);

    // The shared keys are only trusted when they were stored for this event —
    // every event the host opens writes them. The QR image is held to the same
    // rule: it used to be taken whatever event it came from, so a card prepared
    // before the fetch answered could carry the previous event's code.
    const storedForThisEvent = String(localStorage.getItem(INVITE_HASH_EVENT_KEY)) === String(this.eventId);
    if (!this.collabLink && storedForThisEvent) {
      const hash = localStorage.getItem('event_hash');
      if (hash && hash !== 'undefined') this.collabLink = inviteLinkFor(hash);
    }
    if (!this.collabQr && storedForThisEvent && serverQrMatchesInviteLink
        && this.collabLink === inviteLinkFor(localStorage.getItem('event_hash'))) {
      const qr = localStorage.getItem('web_qrImage') || localStorage.getItem('qrImage') || '';
      this.collabQr = qr !== 'undefined' ? qr : '';
    }
    // The card can be ready for a tap before the fetch comes back.
    if (this.collabQr) this.prepareCard();
    this.drawMissingQrs();
  }

  /**
   * Draws the QR for any link on screen that doesn't have one yet. Same URL
   * the server encodes (see QrRenderService), so scans still count against it.
   * Plain square ink, like the server's codes, so the printed card looks the
   * same whichever drew it.
   */
  private async drawMissingQrs(): Promise<void> {
    const draw = (url: string) => this.qrRender
      .render(url, { size: 1024, style: 'square', ink: '#000000', logo: false })
      .catch(() => '');
    const collabLink = this.collabLink;
    const viewerLink = this.viewerLink;
    const [collabQr, viewerQr] = await Promise.all([
      collabLink && !this.collabQr ? draw(collabLink) : Promise.resolve(''),
      viewerLink && !this.viewerQr ? draw(viewerLink) : Promise.resolve(''),
    ]);
    // Only for the link it was drawn from, and only into an empty slot.
    let drawn = false;
    if (collabQr && !this.collabQr && this.collabLink === collabLink) { this.collabQr = collabQr; drawn = true; }
    if (viewerQr && !this.viewerQr && this.viewerLink === viewerLink) { this.viewerQr = viewerQr; drawn = true; }
    if (drawn) this.prepareCard();
  }

  /**
   * Fetches the server's copy of both codes behind whatever seedFromKnownCodes
   * already put on screen, to fill in a link we didn't have and keep the
   * localStorage keys older screens (invite dialog, event-detail) read.
   */
  private loadInviteDetails(attempt = 0): void {
    this.fetchFailed = false;
    this.qrSub?.unsubscribe();
    this.qrLoading = true;

    // The request can hang rather than fail — no timeout on the HTTP call, and
    // the token-refresh branch can stall — which used to leave the button on
    // "Preparing your link…" forever on the first open. A timeout turns that
    // into an error so the retry below actually runs.
    this.qrSub = this.eventService.getEventQR(this.eventId).pipe(
      timeout(InviteShareComponent.QR_TIMEOUT_MS),
    ).subscribe({
      // zone.run: the token-refresh path can resolve outside Angular's zone,
      // and then the link is set but never drawn.
      next: (res: any) => this.zone.run(() => {
        this.qrLoading = false;
        if (!res?.hash) {
          this.onLinkError(attempt, 'missing_hash');
          return;
        }

        localStorage.setItem('event_hash', res.hash);
        localStorage.setItem(INVITE_HASH_EVENT_KEY, String(this.eventId));
        if (res.viewer_hash) localStorage.setItem('viewer_hash', res.viewer_hash);
        if (res.web_qrCode) localStorage.setItem('web_qrImage', res.web_qrCode);
        if (res.viewer_qrCode) localStorage.setItem('viewer_qrImage', res.viewer_qrCode);
        if (res.event_name) localStorage.setItem('name', res.event_name);
        if (res.eventStatus) localStorage.setItem('eventStatus', res.eventStatus);

        // A past event's codes here are fresh expiring ones, not the event's
        // own; only the lasting ones are worth keeping for next time.
        if (res.eventStatus !== 'Past') this.inviteCodes.remember(this.eventId, res.hash, res.viewer_hash);

        // A link already on screen stays, with its own QR. For a past event the
        // seeded code is the event hash while getEventQR answers with a separate
        // expiring code; both work, but swapping a second later changed the link
        // (and its QR) under a host who may already have copied it. The server's
        // QR is only taken for the link it encodes (web_qrCode and
        // web_viewer_qrCode encode https://i.picaggo.com/{code}), so the card
        // never pairs one code's link with another's QR.
        const before = `${this.collabLink}|${this.collabQr.length}|${this.viewerLink}|${this.viewerQr.length}`;
        const fetchedLink = inviteLinkFor(res.hash);
        if (!this.collabLink) this.collabLink = fetchedLink;
        if (!this.collabQr && serverQrMatchesInviteLink && this.collabLink === fetchedLink) {
          this.collabQr = res.web_qrCode || res.qrCode || '';
        }

        let accessReset = false;
        if (res.viewer_hash) {
          const fetchedViewer = inviteLinkFor(res.viewer_hash);
          if (!this.viewerLink) this.viewerLink = fetchedViewer;
          if (!this.viewerQr && serverQrMatchesInviteLink && this.viewerLink === fetchedViewer) {
            this.viewerQr = res.web_viewer_qrCode || res.viewer_qrCode || '';
          }
        } else if (!this.viewerLink) {
          this.viewerUnavailable = true;
          accessReset = this.access === 'view';
          this.access = 'upload';
        }

        // Rasterise the card again only if what it shows changed.
        const after = `${this.collabLink}|${this.collabQr.length}|${this.viewerLink}|${this.viewerQr.length}`;
        if (before !== after || accessReset) this.prepareCard();
        this.drawMissingQrs();
      }),
      error: (err: any) => this.zone.run(() =>
        this.onLinkError(attempt, err?.name === 'TimeoutError' ? 'timeout' : 'qr_fetch_failed')),
    });
  }

  /**
   * The first request right after landing on event-detail can lose a race
   * with the auth token refreshing. Retry quietly a couple of times; after
   * that, offer "Try again" rather than a button that never stops preparing.
   */
  private onLinkError(attempt: number, reason: string): void {
    if (attempt < InviteShareComponent.QR_MAX_RETRIES) {
      setTimeout(() => this.loadInviteDetails(attempt + 1), 800 * (attempt + 1));
      return;
    }
    this.qrLoading = false;
    // A collaborator link seeded from an earlier fetch for this event is still
    // good; linkFailed only shows "Try again" when the selected link is missing.
    this.fetchFailed = true;
    this.analytics.capture('invite_link_failed', { reason, attempts: attempt + 1, surface: this.surface });
  }

  /** "Try again" on the hero button once the automatic retries are spent. */
  retryLink(): void {
    this.analytics.capture('invite_link_retry_tapped', { surface: this.surface });
    this.loadInviteDetails();
  }

  // ── share actions ─────────────────────────────────────────────────────
  private base64ToBlob(base64: string, type: string): Blob {
    const clean = base64.replace(/^data:image\/[a-z]+;base64,/, '').trim();
    const bytes = atob(clean);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new Blob([arr], { type });
  }

  /** The server's QRs are JPEGs; the ones drawn here (drawMissingQrs) are PNGs. */
  private get qrIsPng(): boolean {
    return this.inviteQr.startsWith('data:image/png');
  }

  private qrFileName(): string {
    const name = (this.eventName || 'event').replace(/\s+/g, '-');
    return `${name}-qr.${this.qrIsPng ? 'png' : 'jpg'}`;
  }

  private cardFileName(): string {
    const name = (this.eventName || 'event').replace(/\s+/g, '-');
    return `${name}-invite.png`;
  }

  /**
   * The message that travels with the link into a chat or an inbox.
   * Catalog: invite.share.message.upload / invite.share.message.view.
   */
  private get shareText(): string {
    // Catalog: invite.share.message.b / invite.share.message.b.unnamed. B only,
    // and only with the collaborator link (B has no view-only switch).
    if (this.inviteB && this.access === 'upload') {
      return this.eventName
        ? `Add your photos from ${this.eventName} here, so every moment is in one place.`
        : 'Add your photos here, so every moment is in one place.';
    }
    if (this.access === 'view') {
      // Same words as the printed card's eyebrow (invite.card.eyebrow.view).
      return this.eventName
        ? `You're invited to view the ${this.eventName} photos:`
        : `You're invited to view the event photos:`;
    }
    return `You're invited to share photos from ${this.eventName || 'our event'}`;
  }

  /**
   * The WhatsApp route rides along only with the collaborator link. It is a
   * way to add photos, and the view-only link promises looking only.
   */
  private get offersWhatsapp(): boolean {
    return this.access === 'upload' && !!this.whatsappUploadUrl;
  }

  /**
   * What travels with the *card image*. A Web Share carrying `files` has no
   * url the OS will preview — the picture is the preview — so the link has to
   * ride in the text or it doesn't go at all. Without this the host sends a
   * photo of a QR code and nothing a guest can tap.
   *
   * Some targets drop `text` when `files` is present, so this isn't a given
   * everywhere. iOS WhatsApp — the case we most expected to lose it, and the
   * one that matters most for us — keeps it: checked on a real iPhone
   * 2026-10-20, the card arrives as a picture with a tappable link beneath.
   * saveLinkAlongside stays as the backstop for targets that do drop it: the
   * link is on the clipboard either way, so a host who finds nothing
   * pasteable can paste it.
   */
  private get cardShareText(): string {
    return this.inviteLink
      ? `${this.shareText}\n${this.inviteLink}${this.whatsappUploadLine}`
      : this.shareText;
  }

  /**
   * The WhatsApp route, worded for the guest rather than the host. Only ever
   * appended — a guest who ignores it still has a working link above it.
   */
  private get whatsappUploadLine(): string {
    return this.offersWhatsapp
      // Catalog: invite.whatsapp.assistant
      ? `\n\nOr send your photos via the Picaggo WhatsApp Assistant: ${this.whatsappLinkText}`
      : '';
  }

  /**
   * The WhatsApp link as it's written into a message: without `https://`.
   * Chat apps still make w.picaggo.com/… tappable, but a full URL gets a
   * preview of its own, and the invite should carry exactly one — the album
   * link's card. In iMessage a second preview also split the message apart.
   */
  private get whatsappLinkText(): string {
    return this.whatsappUploadUrl.replace(/^https?:\/\//i, '');
  }

  /**
   * One event for every way an invite leaves this step. `channel` is what the
   * host actually pressed — without it every target collapses into the same
   * `invite_sent` row and "which one do hosts use?" stays unanswerable.
   *
   * For whatsapp/sms/email this records the *handoff*, not a delivery: once the
   * other app opens we can't see whether the host pressed send.
   *
   * Only for actions that hand over the link. Saving the card or the QR is not
   * an invite (a saved picture has nothing a guest can tap) and goes through
   * trackDownload instead. Until 2026-10-23 saves were logged here as
   * channel 'save_card', which inflated the send rate; exclude that channel
   * when reading older data.
   */
  private trackInviteSent(
    method: 'copy' | 'share' | 'download',
    format: 'link' | 'qr' | 'card',
    channel: string,
    extra?: Record<string, any>,
  ): void {
    this.session.shareCount++;
    this.session.channels.add(channel);
    this.analytics.capture('invite_sent', {
      method,
      format,
      channel,
      link_type: this.access === 'view' ? 'web_viewer' : 'web_collaborator',
      surface: this.surface,
      template: this.selectedTemplateId,
      // Did the card beat the upload? false = guests got the plain-cover
      // preview; null = card previews are off, so there was nothing to race.
      card_ready: this.inviteCardService.enabled && this.newInviteDesignEnabled
        ? this.lastPreviewSignature === this.previewSignature
        : null,
      cover_source: this.coverOverride ? 'changed' : this.coverImageUrl ? 'event' : 'fallback',
      // Which layout the host saw (India: "Send on WhatsApp" is the red button).
      india_host: this.indiaHost,
      ...this.abProps,
      ...extra,
    });
  }

  /**
   * A saved card or QR image. Its own events rather than `invite_sent`: a
   * download sends nothing, so it neither counts as an invite nor marks the
   * session as shared.
   */
  private trackDownload(format: 'card' | 'qr', extra?: Record<string, any>): void {
    this.session.channels.add(format === 'card' ? 'save_card' : 'save_qr');
    this.analytics.capture(format === 'card' ? 'invite_card_downloaded' : 'invite_qr_downloaded', {
      surface: this.surface,
      template: this.selectedTemplateId,
      india_host: this.indiaHost,
      ...this.abProps,
      ...extra,
    });
  }

  /**
   * html2canvas can't rasterise a CSS background-image once it fails CORS —
   * it doesn't throw, it just leaves that area blank. Inlining the photo as a
   * data: URI on the detached clone sidesteps that, since html2canvas can
   * always read a same-document data URI.
   *
   * `cache: 'no-store'` is what makes this work on event-detail: that page has
   * already shown the cover as a plain (non-CORS) image, so the browser holds a
   * cached copy without Access-Control-Allow-Origin, and a normal fetch is
   * served that copy and fails CORS even though CloudFront would answer a real
   * CORS request fine.
   *
   * Every photo layer (`.ic-ph` — one in most templates, three in Stack) is
   * pre-cropped to its own box rather than left to html2canvas's own
   * `background-size: cover`, which it gets wrong on a tall/wide box and tiles
   * the photo instead. A layer's CSS filter (Stack's grey and sepia back
   * prints) is baked in for the same reason — html2canvas ignores `filter`.
   *
   * Failures (offline, a genuinely broken URL) are swallowed — the export just
   * keeps the layer as it was.
   */
  private async inlineCoverPhotos(root: HTMLElement, pixelScale: number): Promise<void> {
    const layers = Array.from(root.querySelectorAll<HTMLElement>('.ic-ph'));
    if (!layers.length) return;
    try {
      const blob = await this.fetchCoverBlob(this.cardCover);
      if (!blob) return;
      const bitmap = await createImageBitmap(blob);
      for (const layer of layers) {
        const boxW = layer.offsetWidth;
        const boxH = layer.offsetHeight;
        if (!boxW || !boxH) continue;

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(boxW * pixelScale);
        canvas.height = Math.round(boxH * pixelScale);
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        // object-fit: cover, centred — the same crop the on-screen card shows.
        const ratio = Math.max(canvas.width / bitmap.width, canvas.height / bitmap.height);
        const drawW = bitmap.width * ratio;
        const drawH = bitmap.height * ratio;
        const filter = getComputedStyle(layer).filter;
        if (filter && filter !== 'none') ctx.filter = filter;
        ctx.drawImage(bitmap, (canvas.width - drawW) / 2, (canvas.height - drawH) / 2, drawW, drawH);

        // backgroundImage, not the `background` shorthand — the shorthand
        // resets every sub-property it doesn't mention (background-repeat
        // included), which would re-tile the exported photo.
        layer.style.backgroundImage = `url(${canvas.toDataURL('image/jpeg', 0.92)})`;
        layer.style.backgroundSize = '100% 100%';
        layer.style.backgroundRepeat = 'no-repeat';
        layer.style.filter = 'none';
      }
      bitmap.close?.();
    } catch {
      // Left as the original URL — see the note above.
    }
  }

  /**
   * Up to three attempts, because event-detail has already loaded this exact URL as a
   * plain <img> by the time the host opens the invite. That non-CORS response
   * can be held by CloudFront's edge (when Origin isn't part of its cache key)
   * and handed back to our CORS request without Access-Control-Allow-Origin —
   * `cache: 'no-store'` only gets past the browser's own cache, not the edge's.
   * A throwaway query param is a distinct edge cache key, so the second try
   * reaches the origin with our Origin header. (It can 403 if the signature
   * covers the query string; then we're no worse off than without it.)
   */
  private async fetchCoverBlob(url: string): Promise<Blob | null> {
    if (url.startsWith('data:') || url.startsWith('blob:') || url === FALLBACK_COVER) {
      return (await fetch(url)).blob();
    }
    const attempts = [url];
    // Another signed URL for the same cover. event-detail swaps its on-screen
    // cover to a freshly signed URL and loads it as a plain <img>, which is the
    // URL it hands us — and the one poisoned at the edge. The copy it stored
    // in localStorage earlier usually carries a different signature, so it is
    // a different edge cache entry; it's also exactly what manage-event
    // exports from, which is why the card worked there and not here. Only
    // trusted when it's this event's — event-detail rewrites it per event.
    const stored = localStorage.getItem('coverImage');
    if (stored && stored !== url && !stored.startsWith('data:')
        && String(localStorage.getItem('eventId')) === String(this.eventId)) {
      attempts.push(stored);
    }
    attempts.push(`${url}${url.includes('?') ? '&' : '?'}_card=${Date.now()}`);

    for (const [i, attempt] of attempts.entries()) {
      try {
        const res = await fetch(attempt, { cache: 'no-store', mode: 'cors' });
        if (res.ok) return await res.blob();
        // console.warn('[invite-card] cover fetch', i, 'returned', res.status); //__CONSOLE_LOG__
      } catch (err) {
        // console.warn('[invite-card] cover fetch', i, 'blocked', err); //__CONSOLE_LOG__
      }
    }
    return null;
  }

  /**
   * Rasterises a detached clone of `el` rather than the live element, so the
   * cover photo can be inlined as a data: URI (see inlineCoverPhotos) without
   * touching the URL the visible card is actually using — and so a card that
   * is currently hidden (the printed card while the invite screen is up, or
   * the other way round) can still be captured.
   *
   * The clone gets a fixed width. Cards are `width: 100%`, and once the clone
   * is re-parented under <body> that means 100% of the viewport — on a wide
   * surface like manage-event the saved card came out as a banner. Pinning it
   * also means every surface exports the same card, whatever size the
   * on-screen preview happens to be. The card units (`--u`, see
   * CardUnitDirective) are re-measured on the clone for the same reason.
   */
  private async captureCard(
    el: HTMLElement,
    opts: { pixelWidth: number; fileName: string; type?: 'image/png' | 'image/jpeg'; background?: string } =
      { pixelWidth: InviteShareComponent.EXPORT_PIXEL_WIDTH, fileName: this.cardFileName() },
  ): Promise<File | null> {
    const cssWidth = InviteShareComponent.EXPORT_CARD_WIDTH;
    const clone = el.cloneNode(true) as HTMLElement;
    clone.classList.add('is-capturing');
    clone.removeAttribute('hidden');
    clone.style.position = 'fixed';
    clone.style.top = '-9999px';
    clone.style.left = '-9999px';
    clone.style.width = `${cssWidth}px`;
    clone.style.maxWidth = 'none';
    // Square edges on the saved image — the rounded corners are an on-screen
    // treatment, and in an image they just leave coloured notches to be trimmed.
    clone.style.borderRadius = '0';
    clone.style.boxShadow = 'none';
    document.body.appendChild(clone);
    try {
      const units = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('.ic-unit'))]
        .filter(n => n.classList.contains('ic-unit'));
      for (const n of units) n.style.setProperty('--u', `${n.offsetWidth / 100}px`);

      const pixelScale = opts.pixelWidth / cssWidth;
      await this.inlineCoverPhotos(clone, pixelScale);
      return await this.cardExport.toFile(
        clone, opts.fileName, opts.pixelWidth, opts.background ?? '#FBF0EA', opts.type ?? 'image/png');
    } finally {
      clone.remove();
    }
  }

  /** The card Download/Share act on: the template's printed card, or the legacy one. */
  private get printElement(): HTMLElement | undefined {
    return this.printCardEl?.nativeElement || this.inviteCardEl?.nativeElement;
  }

  /**
   * Rasterises the printed card in the background so the tap doesn't wait on
   * it. The old file is dropped first, so a tap in between captures the card
   * on screen instead of the one it replaced.
   */
  private prepareCard(): void {
    this.cardFile = null;
    setTimeout(async () => {
      const el = this.printElement;
      if (!el) return;
      const key = this.cardKey;
      const file = await this.captureCard(el);
      // The host switched link or look while this rendered: it's stale.
      if (key === this.cardKey) this.cardFile = file;
    }, 0);
  }

  /** What the printed card shows, as far as prepareCard needs to tell renders apart. */
  private get cardKey(): string {
    return `${this.access}|${this.selectedTemplateId}|${this.inviteQr.length}`;
  }

  // ── the card as the link preview ───────────────────────────────────────

  /**
   * Debounced: a host flicking through looks uploads once, after they stop.
   * Must finish before they share — WhatsApp builds the preview on their phone
   * while they type, and the share itself can't wait (see shareFirst). A share
   * inside that window just gets the plain-cover preview, same as today.
   */
  private schedulePreviewSave(trigger: 'initial' | 'template' | 'cover' = 'initial'): void {
    if (!this.newInviteDesignEnabled || !this.inviteCardService.enabled || !this.eventId) return;
    this.previewTrigger = trigger;
    clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this.savePreviewCard(), InviteShareComponent.PREVIEW_SAVE_DELAY_MS);
  }

  private get previewSignature(): string {
    const cover = this.cardCover;
    return [this.selectedTemplateId, cover.length, cover.slice(-48), this.eventName, this.cardDate].join('|');
  }

  private async savePreviewCard(): Promise<void> {
    const el = this.previewCardEl?.nativeElement;
    if (!el) return;
    if (this.previewSaving) { this.previewPending = true; return; }
    const signature = this.previewSignature;
    if (signature === this.lastPreviewSignature) return;

    this.previewSaving = true;
    const template = this.selectedTemplateId;
    const trigger = this.previewTrigger;
    const started = performance.now();
    try {
      const file = await this.captureCard(el, {
        pixelWidth: InviteShareComponent.PREVIEW_PIXEL_WIDTH,
        fileName: this.cardFileName().replace(/\.png$/, '-preview.jpg'),
        type: 'image/jpeg',
        background: '#111111',
      });
      if (!file) throw new Error('render_failed');
      await this.inviteCardService.saveCard(this.eventId, file, template);
      this.lastPreviewSignature = signature;
      this.analytics.capture('invite_card_saved', {
        template, trigger, surface: this.surface, bytes: file.size, ms: Math.round(performance.now() - started),
      });
    } catch (err: any) {
      this.analytics.capture('invite_card_save_failed', {
        template, trigger, surface: this.surface, reason: String(err?.message || err).slice(0, 120),
      });
    } finally {
      this.previewSaving = false;
      if (this.previewPending) {
        this.previewPending = false;
        this.schedulePreviewSave(this.previewTrigger);
      }
    }
  }

  /** Tapping the URL field selects the whole link rather than dropping a caret in it. */
  selectUrl(event: Event): void {
    (event.target as HTMLInputElement)?.select?.();
  }

  /**
   * Saves the card the host is looking at, and puts the invite link on the
   * clipboard with it. A downloaded card is only scannable; the host who
   * pastes it into a chat wants guests to be able to tap too, and asking them
   * to come back here and press Copy is a step most won't take.
   *
   * Falls back to the bare QR when rasterising fails, which is flagged so that
   * degradation stays visible.
   */
  async saveCard(): Promise<void> {
    // Kicked off before the first await, not after. Safari drops transient
    // activation across one and the clipboard then refuses — so the copy has
    // to be what the tap does first, not what we get to once rasterising ends.
    const copying = this.saveLinkAlongside();

    const el = this.printElement;
    const file = this.cardFile || (el ? await this.captureCard(el) : null);
    const linkCopied = await copying;

    if (file) {
      this.cardFile = file;
      this.cardExport.download(file);
      this.trackDownload('card', { link_copied: linkCopied });
    } else {
      this.downloadQr(true);
    }

    // Catalog: invite.card.saved.with-link / invite.card.saved
    this.toastService.showToast(
      linkCopied
        ? "Card saved, and the link's copied. Paste it with the picture so guests can tap instead of scan."
        : 'Card saved.',
      linkCopied ? 4000 : 2000,
    );
  }

  /**
   * Puts the invite link on the clipboard as a passenger on some other action
   * (saving or sharing the card), rather than as a Copy press. Deliberately
   * quiet: no `copied` flag to flash a button the host isn't looking at, and
   * no `invite_sent` row of its own — one tap is one invite, and counting the
   * ride-along separately would inflate every download into two.
   */
  private async saveLinkAlongside(): Promise<boolean> {
    if (!this.inviteLink || !navigator.clipboard) return false;
    try {
      await navigator.clipboard.writeText(this.inviteLink);
      return true;
    } catch {
      return false;
    }
  }

  downloadQr(cardFallback = false): void {
    if (!this.inviteQr) return;
    const blob = this.base64ToBlob(this.inviteQr, this.qrIsPng ? 'image/png' : 'image/jpeg');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = this.qrFileName();
    link.click();
    URL.revokeObjectURL(link.href);
    this.trackDownload('qr', cardFallback ? { card_fallback: true } : undefined);
  }

  /**
   * The WhatsApp link on its own, for a host pasting it into a group they are
   * already in. Separate from Copy so the two don't fight over one `copied`
   * flag and flash the wrong button.
   */
  copyWhatsappUpload(): void {
    if (!this.whatsappUploadUrl) return;
    navigator.clipboard?.writeText(this.whatsappUploadUrl).then(() => {
      this.whatsappCopied = true;
      setTimeout(() => (this.whatsappCopied = false), 2000);
      this.trackInviteSent('copy', 'link', 'whatsapp_upload', { link_type_detail: 'whatsapp_upload' });
    }).catch(() => {
      this.toastService.showToast("Couldn't copy the link. Press and hold it to copy instead.", 2500);
      this.analytics.capture('invite_share_failed', {
        reason: 'clipboard_denied', channel: 'whatsapp_upload', surface: this.surface, ...this.abProps,
      });
    });
  }

  copyLink(channel: string = 'copy_button'): void {
    if (!this.inviteLink) {
      this.analytics.capture('invite_share_failed', { reason: 'missing_link', channel, surface: this.surface, ...this.abProps });
      return;
    }
    navigator.clipboard?.writeText(this.inviteLink).then(() => {
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
      this.trackInviteSent('copy', 'link', channel);
    }).catch(() => {
      this.toastService.showToast("Couldn't copy the link. Press and hold it to copy instead.", 2500);
      this.analytics.capture('invite_share_failed', { reason: 'clipboard_denied', channel, surface: this.surface, ...this.abProps });
    });
  }

  /**
   * Hands the invite to a named app. WhatsApp opens in a tab because wa.me is
   * an https URL that redirects into the app; sms: and mailto: are handled by
   * the OS, so they replace the location rather than opening a dead tab.
   */
  async shareVia(channel: ShareChannel): Promise<void> {
    if (!this.inviteLink) {
      this.analytics.capture('invite_share_failed', { reason: 'missing_link', channel, surface: this.surface, ...this.abProps });
      return;
    }

    // A fast tap can beat the token request; wait for it rather than falling back.
    if (channel === 'whatsapp' && !this.whatsappUploadUrl && this.whatsappUploadLoad) {
      await this.whatsappUploadLoad;
    }

    const text = this.shareText;
    const body = `${text}\n${this.inviteLink}${this.whatsappUploadLine}`;
    const subject = `${this.eventName || 'Our event'} — ${this.access === 'view' ? 'see the photos' : 'add your photos'}`;
    const withWhatsapp = this.offersWhatsapp;

    if (channel === 'more') {
      if (navigator.share) {
        try {
          // The link always goes inside the text, never in `url`. Some targets
          // drop `url` when there is text: the view-only invite, which never
          // has the WhatsApp line, arrived as the sentence with no link. And
          // Android WhatsApp tacks `url` on after the text, which put the album
          // link below the WhatsApp line, so the WhatsApp link was previewed
          // instead. Chat apps preview the first link in the text either way.
          await navigator.share({ title: this.eventName || 'Event', text: body });
          this.trackInviteSent('share', 'link', 'native', { with_whatsapp_upload: withWhatsapp });
        } catch (err: any) {
          // Without this a host who opened the OS sheet and backed out looks
          // identical to one who never pressed More at all.
          this.analytics.capture('invite_share_dismissed', {
            channel: 'native',
            reason: err?.name === 'AbortError' ? 'dismissed' : 'error',
            surface: this.surface,
            ...this.abProps,
          });
        }
        return;
      }
      // No Web Share API (most desktop browsers) — degrade to a copy, marked so
      // those copies aren't mistaken for deliberate Copy presses.
      this.copyLink('more_fallback');
      return;
    }

    if (channel === 'whatsapp') {
      // The invite as a message, through wa.me with no number: WhatsApp (or
      // WhatsApp Web on a computer) opens with the text ready and the host picks
      // the chat. The link is the first URL in the text, so WhatsApp previews
      // it, and that preview is the card. Opened in a tab because wa.me is an
      // https URL that redirects into the app.
      window.open(`https://wa.me/?text=${encodeURIComponent(body)}`, '_blank', 'noopener');
    } else if (channel === 'sms') {
      // The separator is not cosmetic and there is no form both platforms take.
      // RFC 5724 says `sms:<number>?body=`, which is what Android wants; iOS
      // only fills the body from `sms:&body=`. Sending the `?&body=` hybrid to
      // Android makes it read part of the link as a recipient number and drop
      // the body entirely — an empty message addressed to nobody.
      const separator = isIOS() ? '&' : '?';
      // Single line, no `\n`: some SMS composers truncate at the first %0A.
      const smsWhatsapp = this.offersWhatsapp
        ? ` Or send your photos via the Picaggo WhatsApp Assistant: ${this.whatsappLinkText}`
        : '';
      const smsBody = `${text} ${this.inviteLink}${smsWhatsapp}`;
      window.location.href = `sms:${separator}body=${encodeURIComponent(smsBody)}`;
    } else {
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    this.trackInviteSent('share', 'link', channel, { with_whatsapp_upload: withWhatsapp });
  }

  /** Dialog mode only — the sheet owns its own chrome. */
  done(): void {
    this.dialogRef?.close();
  }

  /**
   * The header's X. On the printed screen it steps back to the invite rather
   * than closing: printed cards are one level inside this sheet, and a host who
   * taps X there means "I'm done looking at these", not "throw away the invite
   * I haven't sent yet". Closing from there is still two taps, X then X.
   *
   * Escape and a backdrop tap still close outright — neither reads as "back",
   * and on mobile, where this sheet mostly lives, there is no Escape key.
   */
  closeOrBack(): void {
    if (this.screen === 'printed') {
      this.goBackToInvite();
      return;
    }
    this.done();
  }

  /**
   * Re-rasterises on selection so Save/Share always pick up the look the host
   * is looking at, and re-uploads the link-preview card once they settle.
   * `via` tells thumbnail taps from swipes/arrows in analytics.
   */
  selectTemplate(id: CardTemplateId, via: 'thumb' | 'swipe' | 'arrow' = 'thumb'): void {
    if (id === this.selectedTemplateId) return;
    const from = this.selectedTemplateId;
    this.selectedTemplateId = id;
    this.session.templatesTried.add(id);
    this.analytics.capture('invite_theme_selected', { theme: id, from, via, surface: this.surface });
    this.cardFile = null;
    this.prepareCard();
    this.schedulePreviewSave('template');
  }

  /**
   * The "Guests can" switch. Re-rasterises the printed card, which carries
   * the selected link's QR and scan line.
   */
  setAccess(access: 'upload' | 'view'): void {
    if (access === this.access) return;
    if (access === 'view' && !this.viewerInviteEnabled) return;
    this.access = access;
    this.copied = false;
    if (access === 'view') this.session.viewOnlySelected = true;
    this.analytics.capture('invite_access_selected', {
      access, surface: this.surface, link_ready: !!this.inviteLink,
    });
    this.prepareCard();
  }

  /** Previous/next look, wrapping around — the card's arrows and swipe. */
  stepTemplate(delta: number, via: 'swipe' | 'arrow' = 'arrow'): void {
    const i = this.templates.findIndex(t => t.id === this.selectedTemplateId);
    const next = this.templates[(i + delta + this.templates.length) % this.templates.length];
    this.selectTemplate(next.id, via);
  }

  onCardPointerDown(e: PointerEvent): void {
    this.swipeStartX = e.clientX;
  }

  onCardPointerUp(e: PointerEvent): void {
    if (this.swipeStartX === null) return;
    const dx = e.clientX - this.swipeStartX;
    this.swipeStartX = null;
    if (Math.abs(dx) > 40) {
      this.swipedAt = Date.now();
      this.stepTemplate(dx < 0 ? 1 : -1, 'swipe');
    }
  }

  /** Tap to see the card full size — but not the tap that ends a swipe. */
  openZoom(): void {
    if (Date.now() - this.swipedAt < 400) return;
    this.zoomOpen = true;
    this.session.zoomed = true;
  }

  closeZoom(): void {
    this.zoomOpen = false;
  }

  /**
   * Changes the event's cover from the card, through the same picker the
   * create flow uses (CoverImageComponent), so the stock covers live in one
   * place. It is the event's real cover: the gallery shows it too.
   */
  openCoverPicker(): void {
    if (!this.canEditCover || this.coverSaving) return;
    this.session.coverPickerOpened = true;

    // CoverImageComponent jumps to the edit-event page after saving when this
    // flag is set — its own flow, not this one. Set it aside while it's open.
    const editEvent = localStorage.getItem('editEvent');
    if (editEvent !== null) localStorage.removeItem('editEvent');

    this.dialog.open(CoverImageComponent, {
      width: '500px',
      backdropClass: 'secondary-backdrop',
      panelClass: 'cover-dialog',
      hasBackdrop: true,
      data: { eventName: this.eventName },
    }).afterClosed().subscribe(async (res: { coverImage?: string } | undefined) => {
      if (editEvent !== null) localStorage.setItem('editEvent', editEvent);
      // The picker also leaves its result in localStorage for the create flow
      // to read; it's handed back directly here, so don't leave it lying around.
      localStorage.removeItem('cover');

      const cover = res?.coverImage;
      if (!cover) return;
      const previous = this.coverOverride;
      this.coverOverride = cover;
      this.coverSaving = true;
      try {
        await this.inviteCardService.saveCover(this.eventId, cover);
        this.toastService.showToast('Cover updated. Your album uses it too', 2500);
        this.session.coverChanged = true;
        this.analytics.capture('invite_cover_changed', { surface: this.surface });
        this.cardFile = null;
        this.prepareCard();
        this.schedulePreviewSave('cover');
      } catch (err: any) {
        this.coverOverride = previous;
        this.toastService.showToast("Couldn't save that cover. Check your connection and try again.", 3000);
        this.analytics.capture('invite_cover_change_failed', {
          surface: this.surface, reason: String(err?.message || err).slice(0, 120),
        });
      } finally {
        this.coverSaving = false;
      }
    });
  }

  // ── printed cards ─────────────────────────────────────────────────────
  goToPrintedCards(): void {
    this.session.printedOpened = true;
    this.analytics.capture('invite_printed_cards_opened', { surface: this.surface });
    this.screen = 'printed';
  }

  goBackToInvite(): void {
    this.screen = 'invite';
  }

  /**
   * Shares the same rasterised card as `saveCard()`, through the OS share
   * sheet instead of a download. Falls back to the plain link share when the
   * platform can't share files (most desktop browsers) rather than leaving
   * the button dead.
   */
  async shareCard(): Promise<void> {
    const copying = this.saveLinkAlongside();

    const el = this.printElement;
    const file = this.cardFile || (el ? await this.captureCard(el) : null);

    if (!file || !this.cardExport.canShareFile(file)) {
      this.shareVia('more');
      return;
    }
    this.cardFile = file;
    const shared = await this.cardExport.share(file, this.eventName || 'Event', this.cardShareText);
    const linkCopied = await copying;
    if (shared) this.trackInviteSent('share', 'card', 'native_card', { link_copied: linkCopied });
  }

  /**
   * The WhatsApp button, red for hosts in India. Opens WhatsApp itself (the
   * app on a phone, WhatsApp Web on a computer) with the invite message ready;
   * the host only picks the chat. Guests still see the card: it is the link's
   * preview. Sharing the card *picture* would have to go through the OS share
   * sheet, which made this button feel identical to Share invite / More ways.
   */
  async sendOnWhatsApp(): Promise<void> {
    if (!this.inviteLink) return;
    // A fast tap can beat the WhatsApp-route token; the message should carry it.
    if (!this.whatsappUploadUrl && this.whatsappUploadLoad) await this.whatsappUploadLoad;
    await this.shareVia('whatsapp');
  }

  /**
   * Opens the existing card designer, seeded with what we already know. A
   * contained dialog rather than the full-screen takeover the same component
   * uses elsewhere — this entry point is one step inside the invite sheet's
   * flow, not its own destination, so it needs to still read as "on top of"
   * that flow rather than replacing it.
   */
  openCardDesigner(): void {
    this.analytics.capture('invite_printed_card_action', { action: 'design_own_card', surface: this.surface });
    const dialogRef = this.dialog.open(InvitationCardDesignerComponent, {
      width: '92vw',
      height: '85vh',
      maxWidth: '960px',
      maxHeight: '680px',
      panelClass: 'card-designer-dialog',
    });
    dialogRef.componentInstance.eventName = this.eventName;
    if (this.inviteQr) dialogRef.componentInstance.qrCodeImage = this.inviteQr;
  }

  openCanvaTemplate(template: CanvaTemplate): void {
    this.analytics.capture('invite_printed_card_action', {
      action: 'canva_template_open', template_id: template.id, surface: this.surface,
    });
    window.open(template.canvaUrl, '_blank', 'noopener,noreferrer');
  }

  /**
   * "See all templates" leaves this screen entirely — the full gallery is a
   * routed page, not a dialog. A dialog can close and navigate itself; the
   * sheet has no dialog of its own, so it asks its host to do both.
   */
  goBrowseTemplates(): void {
    this.analytics.capture('invite_printed_card_action', { action: 'browse_templates', surface: this.surface });
    if (this.isDialog) {
      this.dialogRef?.close();
      this.router.navigate(['/invitation']);
    } else {
      this.browseTemplates.emit();
    }
  }
}
